import type { ViewCommand } from './types/command';

/**
 * Palette mode states.
 *
 * `open` is the single list/search mode. `rootCommandId` is null in
 * the main case (full registry searchable) and set to a command id
 * when entered (only that command's children are visible -- "focused"
 * in old vocabulary). The collapse mirrors kbar's exact shape.
 *
 * `view` stays a distinct mode because views replace the entire body
 * (no listbox), with its own non-destructive `queryAtEntry` snapshot
 * for the Back gesture.
 */
export type PaletteMode =
  | { type: 'closed' }
  | { type: 'open'; query: string; rootCommandId: string | null }
  | {
      type: 'view';
      command: ViewCommand<unknown>;
      prefill: unknown;
      /**
       * Snapshot of the open-mode query at view entry, restored to
       * the `open` mode on `EXIT_VIEW` so Back feels non-destructive
       * ("I was searching for X, I went into a view, I hit Back, I
       * still see X").
       */
      queryAtEntry: string;
      /**
       * Snapshot of the entered root at view entry, restored on
       * `EXIT_VIEW` so Back keeps the scope the user was in ("I was
       * in @flags, I opened a view, I hit Back, I'm still in @flags").
       */
      rootAtEntry: string | null;
      /**
       * Monotonic id bumped on every `OPEN_VIEW`. Used as a remount
       * key for the view body so a re-open of the *same* view with
       * fresh prefill re-seeds the view (mount-time `defaultValues`
       * would otherwise be ignored on the second open).
       */
      entryId: number;
    };

/**
 * Command palette state. Just the mode -- nothing else. Analytics
 * (timing, trigger, prev-state) were factored out into action
 * callbacks; the reducer doesn't carry side-information any more.
 */
export interface CommandPaletteState {
  mode: PaletteMode;
}

export type CommandPaletteAction =
  | { type: 'OPEN_PALETTE' }
  | { type: 'CLOSE_PALETTE' }
  | { type: 'UPDATE_QUERY'; query: string }
  /**
   * Enter a root by command id. Permissive: works from any prior
   * state (closed, open, future view). Mirrors `OPEN_VIEW`'s pattern.
   * `query` is the new in-mode query; defaults to '' for a fresh
   * entry.
   *
   * Three call sites land here:
   *   - keyboard shortcut on a scope command (palette was closed)
   *   - snap commit `@flags ` from open mode
   *   - Enter on a focused scope row from open mode
   *
   * Same destination state for all three; the action is atomic.
   */
  | { type: 'OPEN_ROOT'; id: string; query?: string }
  /**
   * Exit the currently-entered root back to main. Only meaningful
   * from open mode with a root set; no-op otherwise. The current
   * query is preserved (Esc-ladder semantics: clear query first,
   * then exit on next gesture).
   */
  | { type: 'EXIT_ROOT' }
  /**
   * Enter view mode. `prefill` is the structured payload the view
   * receives via its `.useView().prefill` api. Defaults to undefined.
   * Callers wanting a specific value pass it through
   * `actions.openView(command, prefill)`, which parses against the
   * command's `prefillSchema` before dispatching.
   */
  | { type: 'OPEN_VIEW'; command: ViewCommand<unknown>; prefill?: unknown }
  | { type: 'EXIT_VIEW' };

export const initialCommandPaletteState: CommandPaletteState = {
  mode: { type: 'closed' },
};

export function commandPaletteReducer(state: CommandPaletteState, action: CommandPaletteAction): CommandPaletteState {
  switch (action.type) {
    case 'OPEN_PALETTE': {
      return { mode: { type: 'open', query: '', rootCommandId: null } };
    }

    case 'CLOSE_PALETTE': {
      return { mode: { type: 'closed' } };
    }

    case 'UPDATE_QUERY': {
      if (state.mode.type !== 'open') {
        // View mode owns its own input; closed has no input.
        return state;
      }
      return { mode: { ...state.mode, query: action.query } };
    }

    case 'OPEN_ROOT': {
      // Atomic from any prior state. The keyboard-shortcut path
      // lands here from `closed`; snap commit and Enter-on-scope
      // land here from `open`. All three result in the same
      // `(open, query, rootCommandId: id)` state.
      return { mode: { type: 'open', query: action.query ?? '', rootCommandId: action.id } };
    }

    case 'EXIT_ROOT': {
      // Only meaningful from open mode with a root set. Preserves
      // the current query so the user's text survives the exit
      // (Esc-ladder: query clears first, then exit).
      if (state.mode.type !== 'open' || state.mode.rootCommandId === null) {
        return state;
      }
      return { mode: { ...state.mode, rootCommandId: null } };
    }

    case 'OPEN_VIEW': {
      // Snapshot what Back should restore. From `open`, that's the live
      // query/root. When swapping one view for another (already in
      // `view` mode, e.g. a programmatic `openView`), carry the ORIGINAL
      // snapshots forward so Back still returns to the search/scope the
      // user had before entering the first view -- recomputing from the
      // current `view` mode would reset them to empty. From `closed`,
      // there's nothing to restore.
      const queryAtEntry =
        state.mode.type === 'open' ? state.mode.query : state.mode.type === 'view' ? state.mode.queryAtEntry : '';
      const rootAtEntry =
        state.mode.type === 'open'
          ? state.mode.rootCommandId
          : state.mode.type === 'view'
            ? state.mode.rootAtEntry
            : null;
      // Bump the entry id when re-opening from an existing view so the
      // body remounts and re-seeds from the new prefill; fresh from
      // any other mode starts at 0.
      const entryId = state.mode.type === 'view' ? state.mode.entryId + 1 : 0;
      return {
        mode: {
          type: 'view',
          command: action.command,
          prefill: action.prefill,
          queryAtEntry,
          rootAtEntry,
          entryId,
        },
      };
    }

    case 'EXIT_VIEW': {
      if (state.mode.type !== 'view') {
        return state;
      }
      return { mode: { type: 'open', query: state.mode.queryAtEntry, rootCommandId: state.mode.rootAtEntry } };
    }

    default:
      return state;
  }
}

/**
 * Selector helpers. All read the canonical shape; nothing here
 * branches on legacy mode names.
 */
export const commandPaletteSelectors = {
  isOpen: (s: CommandPaletteState): boolean => s.mode.type !== 'closed',

  /**
   * Live query for open mode. Empty string for view (which owns its
   * own input) and closed (which has none). Callers needing to
   * distinguish modes explicitly should use `getViewCommand` /
   * `mode.type` directly.
   */
  getQuery: (s: CommandPaletteState): string => (s.mode.type === 'open' ? s.mode.query : ''),

  /**
   * Entered root id, or null if the palette is in the unfocused main
   * case. Undefined when not in open mode.
   */
  getRootCommandId: (s: CommandPaletteState): string | null | undefined =>
    s.mode.type === 'open' ? s.mode.rootCommandId : undefined,

  getViewCommand: (s: CommandPaletteState): ViewCommand<unknown> | undefined =>
    s.mode.type === 'view' ? s.mode.command : undefined,

  getViewPrefill: (s: CommandPaletteState): unknown => (s.mode.type === 'view' ? s.mode.prefill : undefined),

  /**
   * Monotonic id of the current view entry, or undefined when not in
   * view mode. Bumped on every re-open so callers can use it as a
   * remount key.
   */
  getViewEntryId: (s: CommandPaletteState): number | undefined => (s.mode.type === 'view' ? s.mode.entryId : undefined),
};
